import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Mail, Phone, Calendar, Package, Zap, Search, Download, LogOut, Eye, Trash2, CheckCircle, GraduationCap } from 'lucide-react';
import { toast } from 'sonner';
import TrainingPortalAdmin from './TrainingPortalAdmin';

const AdminDashboard = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [contacts, setContacts] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [generatorInquiries, setGeneratorInquiries] = useState([]);
  const [salesRepApplications, setSalesRepApplications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);

  // Simple password check (in production, use proper authentication)
  const handleLogin = (e) => {
    e.preventDefault();
    if (password === 'QNC2025Admin') {
      setIsAuthenticated(true);
      toast.success('Login Successful', {
        description: 'Welcome to QNC Admin Dashboard',
      });
      fetchAllData();
    } else {
      toast.error('Invalid Password', {
        description: 'Please check your password and try again',
      });
    }
  };

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [contactsRes, quotesRes, generatorRes, salesRepRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contacts`).then(res => res.json()),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/quotes`).then(res => res.json()),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/generator-inquiries`).then(res => res.json()),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sales-rep-applications`).then(res => res.json()),
      ]);

      setContacts(contactsRes);
      setQuotes(quotesRes);
      setGeneratorInquiries(generatorRes);
      setSalesRepApplications(salesRepRes);
    } catch (error) {
      toast.error('Failed to load data', {
        description: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setPassword('');
    toast.success('Logged Out Successfully');
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const exportToCSV = (data, filename) => {
    if (!data || data.length === 0) {
      toast.error('No data to export');
      return;
    }

    const headers = Object.keys(data[0]);
    const csvContent = [
      headers.join(','),
      ...data.map(row => headers.map(header => {
        const value = row[header]?.toString() || '';
        return `"${value.replace(/"/g, '""')}"`;
      }).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    
    toast.success('Export Successful', {
      description: `${filename} downloaded`,
    });
  };

  const filterData = (data) => {
    if (!searchTerm) return data;
    return data.filter(item => 
      JSON.stringify(item).toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const handleDelete = async (type, id) => {
    if (!window.confirm('Are you sure you want to delete this submission?')) {
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/${type}/${id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Failed to delete');
      }

      toast.success('Deleted Successfully');
      
      // Update state to remove the deleted item
      if (type === 'contacts') {
        setContacts(contacts.filter(c => c.id !== id));
      } else if (type === 'quotes') {
        setQuotes(quotes.filter(q => q.id !== id));
      } else if (type === 'generator-inquiries') {
        setGeneratorInquiries(generatorInquiries.filter(g => g.id !== id));
      } else if (type === 'sales-rep-applications') {
        setSalesRepApplications(salesRepApplications.filter(s => s.id !== id));
      }
    } catch (error) {
      toast.error('Failed to delete', {
        description: error.message,
      });
    }
  };

  const handleMarkActioned = async (type, id) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/${type}/${id}/action`, {
        method: 'PATCH',
      });

      if (!response.ok) {
        throw new Error('Failed to mark as actioned');
      }

      toast.success('Marked as Actioned');
      
      // Refresh data
      fetchAllData();
    } catch (error) {
      toast.error('Failed to update', {
        description: error.message,
      });
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-2 border-gray-200">
          <CardHeader className="text-center">
            <img src="/qnc-logo.png" alt="QNC Logo" className="h-16 mx-auto mb-4" />
            <CardTitle className="text-2xl">Admin Dashboard</CardTitle>
            <CardDescription>Enter password to access dashboard</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <Input
                type="password"
                placeholder="Admin Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700">
                Login
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            <img src="/qnc-logo.png" alt="QNC Logo" className="h-12" />
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
              <p className="text-gray-600">Manage form submissions and inquiries</p>
            </div>
          </div>
          <Button onClick={handleLogout} variant="outline" className="border-2 border-gray-300">
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="border-2 border-blue-100">
            <CardHeader>
              <CardTitle className="flex items-center text-lg">
                <Mail className="w-5 h-5 mr-2 text-blue-600" />
                Contact Forms
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-4xl font-bold text-blue-600">{contacts.length}</div>
              <p className="text-sm text-gray-600 mt-2">Total submissions</p>
            </CardContent>
          </Card>

          <Card className="border-2 border-green-100">
            <CardHeader>
              <CardTitle className="flex items-center text-lg">
                <Package className="w-5 h-5 mr-2 text-green-600" />
                Quote Requests
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-4xl font-bold text-green-600">{quotes.length}</div>
              <p className="text-sm text-gray-600 mt-2">Pending quotes</p>
            </CardContent>
          </Card>

          <Card className="border-2 border-yellow-100">
            <CardHeader>
              <CardTitle className="flex items-center text-lg">
                <Zap className="w-5 h-5 mr-2 text-yellow-600" />
                Generator Inquiries
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-4xl font-bold text-yellow-600">{generatorInquiries.length}</div>
              <p className="text-sm text-gray-600 mt-2">Generator requests</p>
            </CardContent>
          </Card>

          <Card className="border-2 border-purple-100">
            <CardHeader>
              <CardTitle className="flex items-center text-lg">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 mr-2 text-purple-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
                Sales Rep Apps
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-4xl font-bold text-purple-600">{salesRepApplications.length}</div>
              <p className="text-sm text-gray-600 mt-2">New applications</p>
            </CardContent>
          </Card>
        </div>

        {/* Search Bar */}
        <Card className="mb-6 border-2 border-gray-200">
          <CardContent className="pt-6">
            <div className="flex items-center space-x-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <Input
                  type="text"
                  placeholder="Search by name, email, phone, or message..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Button onClick={fetchAllData} variant="outline" disabled={loading}>
                {loading ? 'Refreshing...' : 'Refresh'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs defaultValue="contacts" className="space-y-6">
          <TabsList className="grid w-full grid-cols-2 lg:grid-cols-5">
            <TabsTrigger value="contacts">Contacts ({contacts.length})</TabsTrigger>
            <TabsTrigger value="quotes">Quotes ({quotes.length})</TabsTrigger>
            <TabsTrigger value="generators">Generators ({generatorInquiries.length})</TabsTrigger>
            <TabsTrigger value="salesreps">Sales Reps ({salesRepApplications.length})</TabsTrigger>
            <TabsTrigger value="training">
              <GraduationCap className="w-4 h-4 mr-1" />
              Training Portal
            </TabsTrigger>
          </TabsList>

          {/* Contact Forms Tab */}
          <TabsContent value="contacts">
            <Card className="border-2 border-gray-200">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Contact Form Submissions</CardTitle>
                  <Button 
                    onClick={() => exportToCSV(contacts, 'contact_forms')}
                    variant="outline"
                    size="sm"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Export CSV
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {filterData(contacts).length === 0 ? (
                    <p className="text-center text-gray-500 py-8">No contact forms found</p>
                  ) : (
                    filterData(contacts).map((contact) => (
                      <Card key={contact.id} className="border border-gray-200 hover:border-blue-300 transition-colors">
                        <CardContent className="pt-6">
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex-1">
                              <h3 className="font-bold text-lg text-gray-900">{contact.name}</h3>
                              <div className="flex items-center space-x-4 mt-2 text-sm text-gray-600">
                                <span className="flex items-center">
                                  <Mail className="w-4 h-4 mr-1" />
                                  {contact.email}
                                </span>
                                <span className="flex items-center">
                                  <Phone className="w-4 h-4 mr-1" />
                                  {contact.phone}
                                </span>
                              </div>
                            </div>
                            <div className="flex flex-col items-end space-y-2">
                              <Badge variant="secondary" className={contact.actioned ? "bg-green-100 text-green-700" : ""}>
                                {contact.actioned ? "Actioned" : contact.status}
                              </Badge>
                            </div>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-4 mb-3">
                            <p className="text-gray-700">{contact.message}</p>
                          </div>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center text-xs text-gray-500">
                              <Calendar className="w-3 h-3 mr-1" />
                              {formatDate(contact.created_at)}
                            </div>
                            <div className="flex space-x-2">
                              {!contact.actioned && (
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                  onClick={() => handleMarkActioned('contacts', contact.id)}
                                >
                                  <CheckCircle className="w-4 h-4 mr-1" />
                                  Mark Actioned
                                </Button>
                              )}
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => handleDelete('contacts', contact.id)}
                              >
                                <Trash2 className="w-4 h-4 mr-1" />
                                Delete
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Quote Requests Tab */}
          <TabsContent value="quotes">
            <Card className="border-2 border-gray-200">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Quote Requests</CardTitle>
                  <Button 
                    onClick={() => exportToCSV(quotes, 'quote_requests')}
                    variant="outline"
                    size="sm"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Export CSV
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {filterData(quotes).length === 0 ? (
                    <p className="text-center text-gray-500 py-8">No quote requests found</p>
                  ) : (
                    filterData(quotes).map((quote) => (
                      <Card key={quote.id} className="border border-gray-200 hover:border-green-300 transition-colors">
                        <CardContent className="pt-6">
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex-1">
                              <h3 className="font-bold text-lg text-gray-900">{quote.name}</h3>
                              <div className="flex items-center space-x-4 mt-2 text-sm text-gray-600">
                                <span className="flex items-center">
                                  <Mail className="w-4 h-4 mr-1" />
                                  {quote.email}
                                </span>
                                <span className="flex items-center">
                                  <Phone className="w-4 h-4 mr-1" />
                                  {quote.phone}
                                </span>
                              </div>
                            </div>
                            <Badge variant="secondary" className={quote.actioned ? "bg-green-100 text-green-700" : ""}>
                              {quote.actioned ? "Actioned" : quote.status}
                            </Badge>
                          </div>
                          <div className="mb-3">
                            <Badge className="bg-blue-100 text-blue-700">
                              Service: {quote.service}
                            </Badge>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-4 mb-3">
                            <p className="text-sm font-semibold text-gray-700 mb-2">Project Details:</p>
                            <p className="text-gray-700">{quote.project_details}</p>
                          </div>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center text-xs text-gray-500">
                              <Calendar className="w-3 h-3 mr-1" />
                              {formatDate(quote.created_at)}
                            </div>
                            <div className="flex space-x-2">
                              {!quote.actioned && (
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                  onClick={() => handleMarkActioned('quotes', quote.id)}
                                >
                                  <CheckCircle className="w-4 h-4 mr-1" />
                                  Mark Actioned
                                </Button>
                              )}
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => handleDelete('quotes', quote.id)}
                              >
                                <Trash2 className="w-4 h-4 mr-1" />
                                Delete
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Generator Inquiries Tab */}
          <TabsContent value="generators">
            <Card className="border-2 border-gray-200">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Generator Inquiries</CardTitle>
                  <Button 
                    onClick={() => exportToCSV(generatorInquiries, 'generator_inquiries')}
                    variant="outline"
                    size="sm"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Export CSV
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {filterData(generatorInquiries).length === 0 ? (
                    <p className="text-center text-gray-500 py-8">No generator inquiries found</p>
                  ) : (
                    filterData(generatorInquiries).map((inquiry) => (
                      <Card key={inquiry.id} className="border border-gray-200 hover:border-yellow-300 transition-colors">
                        <CardContent className="pt-6">
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex-1">
                              <h3 className="font-bold text-lg text-gray-900">
                                {inquiry.name || 'Anonymous'}
                              </h3>
                              {(inquiry.email || inquiry.phone) && (
                                <div className="flex items-center space-x-4 mt-2 text-sm text-gray-600">
                                  {inquiry.email && (
                                    <span className="flex items-center">
                                      <Mail className="w-4 h-4 mr-1" />
                                      {inquiry.email}
                                    </span>
                                  )}
                                  {inquiry.phone && (
                                    <span className="flex items-center">
                                      <Phone className="w-4 h-4 mr-1" />
                                      {inquiry.phone}
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                            <Badge variant="secondary" className={inquiry.actioned ? "bg-green-100 text-green-700" : ""}>
                              {inquiry.actioned ? "Actioned" : inquiry.status}
                            </Badge>
                          </div>
                          <div className="bg-yellow-50 rounded-lg p-4 mb-3">
                            <div className="grid grid-cols-2 gap-4 mb-3">
                              <div>
                                <p className="text-xs text-gray-600 mb-1">Generator Model:</p>
                                <p className="font-semibold text-gray-900">{inquiry.generator_name}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-600 mb-1">Power Output:</p>
                                <p className="font-semibold text-gray-900">{inquiry.power}</p>
                              </div>
                            </div>
                            <div>
                              <p className="text-xs text-gray-600 mb-1">Price:</p>
                              <p className="font-semibold text-blue-600 text-lg">{inquiry.price}</p>
                            </div>
                            {inquiry.message && (
                              <div className="mt-3 pt-3 border-t border-yellow-200">
                                <p className="text-gray-700">{inquiry.message}</p>
                              </div>
                            )}
                          </div>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center text-xs text-gray-500">
                              <Calendar className="w-3 h-3 mr-1" />
                              {formatDate(inquiry.created_at)}
                            </div>
                            <div className="flex space-x-2">
                              {!inquiry.actioned && (
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                  onClick={() => handleMarkActioned('generator-inquiries', inquiry.id)}
                                >
                                  <CheckCircle className="w-4 h-4 mr-1" />
                                  Mark Actioned
                                </Button>
                              )}
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => handleDelete('generator-inquiries', inquiry.id)}
                              >
                                <Trash2 className="w-4 h-4 mr-1" />
                                Delete
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Sales Rep Applications Tab */}
          <TabsContent value="salesreps">
            <Card className="border-2 border-gray-200">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Sales Representative Applications</CardTitle>
                  <Button 
                    onClick={() => exportToCSV(salesRepApplications, 'sales_rep_applications')}
                    variant="outline"
                    size="sm"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Export CSV
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {filterData(salesRepApplications).length === 0 ? (
                    <p className="text-center text-gray-500 py-8">No sales rep applications found</p>
                  ) : (
                    filterData(salesRepApplications).map((application) => (
                      <Card key={application.id} className="border border-gray-200 hover:border-purple-300 transition-colors">
                        <CardContent className="pt-6">
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex-1">
                              <h3 className="font-bold text-lg text-gray-900">
                                {application.full_name}
                              </h3>
                              <div className="flex items-center space-x-4 mt-2 text-sm text-gray-600">
                                <span className="flex items-center">
                                  <Mail className="w-4 h-4 mr-1" />
                                  {application.email}
                                </span>
                                <span className="flex items-center">
                                  <Phone className="w-4 h-4 mr-1" />
                                  {application.phone}
                                </span>
                              </div>
                              <div className="mt-2 text-sm text-gray-600">
                                <span className="inline-flex items-center">
                                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 mr-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg>
                                  {application.location}
                                </span>
                              </div>
                            </div>
                            <Badge variant="secondary" className="bg-purple-100 text-purple-700">
                              {application.status}
                            </Badge>
                          </div>
                          <div className="bg-purple-50 rounded-lg p-4 mb-3">
                            <div className="mb-3">
                              <p className="text-xs text-gray-600 mb-1 font-semibold">Sales Experience:</p>
                              <p className="text-gray-700 text-sm">{application.experience}</p>
                            </div>
                            <div className="mb-3 pt-3 border-t border-purple-200">
                              <p className="text-xs text-gray-600 mb-1 font-semibold">Why They Want to Join:</p>
                              <p className="text-gray-700 text-sm">{application.why_join}</p>
                            </div>
                            <div className="pt-3 border-t border-purple-200">
                              <p className="text-xs text-gray-600 mb-2 font-semibold">Resume:</p>
                              <div className="flex items-center justify-between">
                                <div className="flex items-center text-sm">
                                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 mr-2 text-purple-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline></svg>
                                  <span className="font-medium text-purple-700">{application.resume_filename}</span>
                                </div>
                                <a 
                                  href={`${process.env.REACT_APP_BACKEND_URL}/api/download-resume/${application.id}`}
                                  download
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <Button size="sm" className="bg-purple-600 hover:bg-purple-700">
                                    <Download className="w-4 h-4 mr-1" />
                                    Download
                                  </Button>
                                </a>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center text-xs text-gray-500">
                              <Calendar className="w-3 h-3 mr-1" />
                              {formatDate(application.created_at)}
                            </div>
                            <div className="flex space-x-2">
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => handleDelete('sales-rep-applications', application.id)}
                              >
                                <Trash2 className="w-4 h-4 mr-1" />
                                Delete
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Training Portal Tab */}
          <TabsContent value="training">
            <TrainingPortalAdmin />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboard;
